import React from "react";
import { motion } from "framer-motion";
import HeaderNavMain from "./HeaderNavMain";
import { Link } from "react-router-dom";

const fadeInUp = (delay = 0) => ({
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay },
});

const getRandomX = () =>
  typeof window !== "undefined"
    ? Math.random() * window.innerWidth
    : Math.random() * 400;

const getRandomY = () =>
  typeof window !== "undefined"
    ? Math.random() * window.innerHeight
    : Math.random() * 800;


export default function HeroMain() {
  const metrics = [
    { label: "Downloads", value: "42.0K" },
    { label: "Active Devices", value: "~5.3K" },
    { label: "Releases", value: "v2.2.1" },
  ];

  return (
    <section
      id="hero"
      className="
        relative w-full 
        lg:min-h-screen lg:h-screen
        flex flex-col
        text-center overflow-hidden
        bg-gradient-to-br from-[#1b1b1b] via-[#0a0a0a] to-black
      "
    >
      <HeaderNavMain />

      <video
        src="/assets/videos/video_hero.mp4"
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover opacity-40"
      />

      <div
        className="
          absolute inset-0 backdrop-blur-3xl 
          bg-gradient-to-tr from-indigo-500/10 via-purple-500/10 to-blue-500/5 
          animate-blurPulse
        "
      />

      {/* PARTICLE BACKGROUND */}
      <div className="absolute inset-0 bg-black/60" />
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
        {[...Array(8)].map((_, i) => (
          <motion.span
            key={i}
            className="absolute w-[3px] h-[3px] bg-indigo-400 rounded-full blur-[2px] opacity-40"
            initial={{
              x: getRandomX(),
              y: getRandomY(),
              scale: 0,
            }}
            animate={{
              y: [null, Math.random() * -80],
              opacity: [0.2, 0.6, 0.2],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: 7 + Math.random() * 5,
              repeat: Infinity,
              repeatDelay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <div className="relative z-20 flex-1 flex items-center justify-center w-full">
        {/* MOBILE (< md) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="
            w-full max-w-md px-4 
            flex flex-col items-center justify-center 
            md:hidden mt-[70px] mb-[120px]
          "
        >
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="
              relative 
              text-[26px]
              font-extrabold leading-tight text-center
            "
          >
            <span className="relative bg-gradient-to-r from-indigo-400 via-blue-400 to-purple-400 text-transparent bg-clip-text">
              Puzzle worlds built for<br/>calm, focus, and discovery
            </span>
          </motion.h2>

          <motion.p
            {...fadeInUp(0.2)}
            className="
              mt-4 text-slate-300 
              max-w-xs 
              leading-relaxed 
              text-[13px]
            "
          >
            FloatKeyGames creates geometric puzzle universes where every move
            matters, blending challenge, clarity, and dreamlike beauty.
          </motion.p>

          <motion.div
            {...fadeInUp(0.4)}
            className="
              mt-6 
              flex flex-col 
              gap-3 
              w-full 
              items-stretch 
              justify-center
            "
          >
            <a
              href="https://play.google.com/store/apps/details?id=com.floatkeygames.floatblock"
              target="_blank"
              rel="noopener noreferrer"
              className="
                px-6 py-3 rounded-xl font-semibold text-white 
                bg-gradient-to-r from-indigo-500 to-purple-500 
                shadow-[0_0_20px_rgba(99,102,241,0.4)] 
                hover:shadow-[0_0_25px_rgba(139,92,246,0.6)] 
                hover:scale-105 transition-all duration-300
                w-[250px] max-w-xs mx-auto
              "
            >
              Play Float Block
            </a>

            <Link
              to="/floatblock"
              className="
                px-6 py-3 rounded-xl font-semibold border border-white/30 text-white 
                hover:bg-white/10 hover:border-indigo-400 
                hover:scale-105 transition-all duration-300
                w-[250px] max-w-xs mx-auto 
              "
            >
              Read More
            </Link>
          </motion.div>

          <div
            className="
              mt-8 
              grid grid-cols-3 
              gap-3 
              text-[0.7rem] 
              text-slate-300
            "
          >
            {metrics.map((m, i) => (
              <motion.div key={m.label} {...fadeInUp(0.5 + i * 0.1)}>
                <div className="text-[0.75rem] font-semibold">{m.label}</div>
                <div className="mt-1 text-base font-bold text-white">
                  {m.value}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* TABLET (md ≤ width < lg) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="
            hidden md:flex lg:hidden
            w-full max-w-3xl 
            px-6 
            flex-col items-center justify-center mt-[130px] mb-[200px]
          "
        >
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="
              relative 
              text-4xl 
              font-extrabold leading-tight text-center
            "
          >
            <span
              className="absolute inset-0 blur-3xl opacity-70 animate-glow"
              style={{
                background:
                  "linear-gradient(270deg, #6366f1, #8b5cf6, #3b82f6, #a855f7)",
                backgroundSize: "800% 800%",
                zIndex: 0,
              }}
            />
            <span className="relative bg-gradient-to-r from-indigo-400 via-blue-400 to-purple-400 text-transparent bg-clip-text">
              Puzzle worlds built for
            </span>
            <br />
            <span className="relative bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 text-transparent bg-clip-text">
              calm, focus, and discovery
            </span>
          </motion.h2>

          <motion.p
            {...fadeInUp(0.2)}
            className="
              mt-5 text-slate-300 
              max-w-lg 
              leading-relaxed 
              text-base
            "
          >
            FloatKeyGames creates geometric puzzle universes where every move
            matters, blending challenge, clarity, and dreamlike beauty.
          </motion.p>

          <motion.div
            {...fadeInUp(0.4)}
            className="
              mt-7 
              flex flex-row 
              gap-4 
              items-center 
              justify-center
              w-full
            "
          >
            <a
              href="https://play.google.com/store/apps/details?id=com.floatkeygames.floatblock"
              target="_blank"
              rel="noopener noreferrer"
              className="
                px-7 py-3 rounded-xl font-semibold text-white 
                bg-gradient-to-r from-indigo-500 to-purple-500 
                shadow-[0_0_20px_rgba(99,102,241,0.4)] 
                hover:shadow-[0_0_25px_rgba(139,92,246,0.6)] 
                hover:scale-105 transition-all duration-300
              "
            >
              Play Float Block
            </a>

            <Link
              to="/floatblock"
              className="
                px-7 py-3 rounded-xl font-semibold border border-white/30 text-white 
                hover:bg-white/10 hover:border-indigo-400 
                hover:scale-105 transition-all duration-300
              "
            >
              Read More
            </Link>
          </motion.div>

          <div
            className="
              mt-9 
              grid grid-cols-3 
              gap-4 
              text-xs 
              text-slate-300
            "
          >
            {metrics.map((m, i) => (
              <motion.div key={m.label} {...fadeInUp(0.5 + i * 0.1)}>
                <div className="text-sm font-semibold">{m.label}</div>
                <div className="mt-1 text-lg font-bold text-white">
                  {m.value}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* PC (≥ lg) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="
            hidden lg:flex
            w-full max-w-5xl 
            px-8 
            flex-col items-center justify-center
          "
        >
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="
              relative 
              text-6xl 
              font-extrabold leading-tight text-center
            "
          >
            <span
              className="absolute inset-0 blur-3xl opacity-70 animate-glow"
              style={{
                background:
                  "linear-gradient(270deg, #6366f1, #8b5cf6, #3b82f6, #a855f7)",
                backgroundSize: "800% 800%",
                zIndex: 0,
              }}
            />
            <span className="relative bg-gradient-to-r from-indigo-400 via-blue-400 to-purple-400 text-transparent bg-clip-text">
              Puzzle worlds built for
            </span>
            <br />
            <span className="relative bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 text-transparent bg-clip-text">
              calm, focus, and discovery
            </span>
          </motion.h2>

          <motion.p
            {...fadeInUp(0.2)}
            className="
              mt-6 text-slate-300 
              max-w-2xl 
              leading-relaxed 
              text-lg
            "
          >
            FloatKeyGames creates geometric puzzle universes where every move
            matters, blending challenge, clarity, and dreamlike beauty.
          </motion.p>

          <motion.div
            {...fadeInUp(0.4)}
            className="
              mt-8 
              flex flex-row 
              gap-4 
              items-center 
              justify-center
            "
          >
            <a
              href="https://play.google.com/store/apps/details?id=com.floatkeygames.floatblock"
              target="_blank"
              rel="noopener noreferrer"
              className="
                px-8 py-3.5 rounded-xl font-semibold text-white 
                bg-gradient-to-r from-indigo-500 to-purple-500 
                shadow-[0_0_24px_rgba(99,102,241,0.5)] 
                hover:shadow-[0_0_32px_rgba(139,92,246,0.7)] 
                hover:scale-105 transition-all duration-300
              "
            >
              Play Float Block
            </a>

            <Link
              to="/floatblock"
              className="
                px-8 py-3.5 rounded-xl font-semibold border border-white/30 text-white 
                hover:bg-white/10 hover:border-indigo-400 
                hover:scale-105 transition-all duration-300
              "
            >
              Read More
            </Link>
          </motion.div>

          <div
            className="
              mt-10 
              grid grid-cols-3 
              gap-6 
              text-sm 
              text-slate-300
            "
          >
            {metrics.map((m, i) => (
              <motion.div key={m.label} {...fadeInUp(0.5 + i * 0.1)}>
                <div className="text-sm font-semibold tracking-wide">
                  {m.label}
                </div>
                <div className="mt-1 text-2xl font-bold text-white">
                  {m.value}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
     <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-indigo-500/70 to-transparent" />

      
    </section>
  );
}
